import { CalendarClock, Pencil, Send, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ActionSheet } from '@/components/mobile';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ScheduledMessageDialogs from './ScheduledMessageDialogs';
import { formatScheduledDate } from './useScheduledMessages';

/**
 * Renders upcoming scheduled messages inline at the bottom of the conversation
 * thread, so teammates can edit, reschedule, send now, or cancel them without
 * leaving the conversation (TK-87). Only applies to 1:1 conversations.
 *
 * State/actions come from a `useScheduledMessages` instance owned by ChatFeed,
 * so the thread and the bottom list share a single source of truth (ChatFeed
 * also uses it to decide which scheduled events to hide from the thread).
 */
const ThreadScheduledMessages = ({
  scheduledMessages,
  activeActionId,
  sendNow,
  deleteScheduledMessage,
  dialogs,
}) => {
  const [confirmation, setConfirmation] = useState(null);
  // Phones: a tap opens an action sheet instead of the dropdown.
  const isMobile = useIsMobile();
  const [sheetMessage, setSheetMessage] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  if (!scheduledMessages || scheduledMessages.length === 0) {
    return null;
  }

  const confirmAction = () => {
    if (confirmation?.type === 'send') {
      sendNow(confirmation.scheduledMessage);
    } else if (confirmation?.type === 'delete') {
      deleteScheduledMessage(confirmation.scheduledMessage);
    }
    setConfirmation(null);
  };

  // Soonest first, so the queue reads top-to-bottom in send order.
  const sortedScheduledMessages = [...scheduledMessages].sort(
    (a, b) => new Date(a.scheduledSendDate) - new Date(b.scheduledSendDate),
  );

  return (
    <div className="flex flex-col items-stretch gap-1.5 px-4 pb-2 pt-1">
      <ScheduledMessageDialogs dialogs={dialogs} />

      {sortedScheduledMessages.map((scheduledMessage) => {
        const isActionLoading = activeActionId === scheduledMessage._id;
        const rowLabel = scheduledMessage.textingBlocked
          ? 'Sending stopped — texting was unavailable'
          : `Sending ${formatScheduledDate(scheduledMessage.scheduledSendDate)}`;

        if (isMobile) {
          return (
            <button
              key={scheduledMessage._id}
              type="button"
              disabled={isActionLoading}
              aria-haspopup="dialog"
              aria-label={`${rowLabel}. ${scheduledMessage.text}. Options`}
              onClick={() => {
                setSheetMessage(scheduledMessage);
                setSheetOpen(true);
              }}
              className="flex min-h-11 w-full items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2 text-left text-sm shadow-[0_1px_2px_rgba(148,163,184,0.12)] active:bg-accent disabled:opacity-50"
            >
              <CalendarClock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-foreground">{rowLabel}</span>
                <span className="block truncate text-muted-foreground">{scheduledMessage.text}</span>
              </span>
            </button>
          );
        }

        return (
          <DropdownMenu key={scheduledMessage._id}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={isActionLoading}
                aria-label={scheduledMessage.textingBlocked ? 'Sending stopped. Click for options.' : 'Scheduled message. Click for options.'}
                className="flex w-full items-center gap-2 rounded-xl border border-border bg-card px-4 py-2 text-left text-sm shadow-[0_1px_2px_rgba(148,163,184,0.12)] transition-colors hover:bg-accent disabled:opacity-50"
              >
                <CalendarClock className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 truncate">
                  <span className="font-medium text-foreground">
                    {scheduledMessage.textingBlocked ? 'Sending stopped — texting was unavailable' : `Sending ${formatScheduledDate(scheduledMessage.scheduledSendDate)}`}
                  </span>
                  <span className="mx-1.5 text-muted-foreground/60">·</span>
                  <span className="text-muted-foreground">
                    {scheduledMessage.text}
                  </span>
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" side="top">
                <DropdownMenuItem disabled={dialogs.sendingBlocked || Boolean(scheduledMessage.textingBlocked)} onSelect={() => dialogs.openEditDialog(scheduledMessage)}>
                  <Pencil className="size-4" />
                  Edit text
                </DropdownMenuItem>
                <DropdownMenuItem disabled={dialogs.sendingBlocked || Boolean(scheduledMessage.textingBlocked)} onSelect={() => dialogs.openRescheduleDialog(scheduledMessage)}>
                  <CalendarClock className="size-4" />
                  Change time
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={dialogs.sendingBlocked || Boolean(scheduledMessage.textingBlocked)}
                  onSelect={() => setConfirmation({ type: 'send', scheduledMessage })}
                >
                  <Send className="size-4" />
                  Send now
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={() => setConfirmation({ type: 'delete', scheduledMessage })}
                >
                  <Trash2 className="size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
        );
      })}

      {isMobile && sheetMessage && (
        <ActionSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          title="Scheduled message"
          header={
            <div className="mb-3 rounded-xl bg-background px-4 py-3 dark:bg-zinc-900">
              <p className="text-[13px] font-medium text-muted-foreground">
                {sheetMessage.textingBlocked
                  ? 'Sending stopped — texting was unavailable'
                  : `Sending ${formatScheduledDate(sheetMessage.scheduledSendDate)}`}
              </p>
              <p className="mt-1 line-clamp-4 whitespace-pre-wrap break-words text-[15px] leading-snug">
                {sheetMessage.text}
              </p>
            </div>
          }
          sections={[
            {
              id: 'edit',
              items: [
                {
                  id: 'edit',
                  label: 'Edit text',
                  icon: Pencil,
                  disabled: dialogs.sendingBlocked || Boolean(sheetMessage.textingBlocked),
                  onSelect: () => dialogs.openEditDialog(sheetMessage),
                },
                {
                  id: 'time',
                  label: 'Change time',
                  icon: CalendarClock,
                  disabled: dialogs.sendingBlocked || Boolean(sheetMessage.textingBlocked),
                  onSelect: () => dialogs.openRescheduleDialog(sheetMessage),
                },
                {
                  id: 'send',
                  label: 'Send now',
                  icon: Send,
                  disabled: dialogs.sendingBlocked || Boolean(sheetMessage.textingBlocked),
                  onSelect: () => setConfirmation({ type: 'send', scheduledMessage: sheetMessage }),
                },
              ],
            },
            {
              id: 'delete',
              items: [
                {
                  id: 'delete',
                  label: 'Delete',
                  icon: Trash2,
                  destructive: true,
                  onSelect: () => setConfirmation({ type: 'delete', scheduledMessage: sheetMessage }),
                },
              ],
            },
          ]}
        />
      )}

      <AlertDialog
        open={!!confirmation}
        onOpenChange={(open) => {
          if (!open) setConfirmation(null);
        }}
      >
        <AlertDialogContent className="font-sans">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmation?.type === 'delete'
                ? 'Delete this scheduled message?'
                : 'Send this message now?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmation?.type === 'delete'
                ? 'The customer will not receive this message.'
                : 'Move it to the top of the queue and send within the next 10 minutes.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {confirmation?.type === 'delete' ? 'Cancel' : 'Keep scheduled'}
            </AlertDialogCancel>
            <AlertDialogAction
              className={
                confirmation?.type === 'delete'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : undefined
              }
              disabled={confirmation?.type === 'send' && dialogs.sendingBlocked}
              onClick={confirmAction}
            >
              {confirmation?.type === 'delete' ? 'Delete message' : 'Send now'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ThreadScheduledMessages;
